import React from 'react';
import PropTypes from 'prop-types';
import graphql from "babel-plugin-relay/macro";
import {useFragment} from "react-relay";
import {
    DateCell,
    PhoneCell
} from "../../../components/common/advance-table/CustomCells";
import {useUser} from "../../../hooks/useUser";
import {Card} from "react-bootstrap";
import CardTableHeader from "../../../components/common/headers/CardTableHeader";
import AdvanceTable from "../../../components/common/advance-table/AdvanceTable";
import AdvanceTablePagination from "../../../components/common/advance-table/AdvanceTablePagination";
import AdvanceTableWrapper from "../../../components/common/advance-table/AdvanceTableWrapper";
import NewRemittanceAddressModal from "./NewRemittanceAddressModal";
import EditRemittanceAddressModal from "./EditRemittanceAddressModal";
import DeleteModal from "../../../components/common/modals/DeleteModal";
import {FormatAddress} from "../../../helpers/utils";
import {useTableModals} from "../../../hooks/useTableModals";

const cols = [
    {accessor: "npi", Header: "NPI"},
    {accessor: "tin", Header: "TIN"},
    {accessor: "lineOfService.lineOfService.name", Header: "Line of Service"},
    {accessor: "name", Header: "Name"},
    {
        accessor: "addressLine1", Header: "Address",
        Cell: (rowData) => {
            const address = rowData.row.original
            return <>{FormatAddress(address.addressLine1, address.addressLine2, address.addressLine3,
                address.city, address.state, address.postalCode)}</>
        }
    },
    {accessor: "phoneNumber", Header: "Phone Number", Cell: PhoneCell},
    {accessor: "effectiveFrom", Header: "Effective From", Cell: DateCell},
    {accessor: "effectiveTo", Header: "Effective To", Cell: DateCell}
]

const RemittanceAddresses = ({remittanceAddresses, billingGroupId, availableLinesOfService}) => {
    const deleteMutation = graphql`
    mutation RemittanceAddressesDeleteMutation($id: ID!) {
        deleteNode(input: {id: $id}) {
            deletedNodeId @deleteRecord            
            owner {
                ... on BillingGroup {
                    id
                    remittanceAddresses {
                        id
                    }}}}}`
    const data = useFragment(graphql`
        fragment RemittanceAddresses_Addresses on RemittanceAddress @relay(plural:true) {
            id
            lineOfService {
              id
              lineOfService {
                  name
              }
            }
            npi
            tin
            billingPermitted
            name
            addressLine1
            addressLine2
            addressLine3
            city
            state
            postalCode
            county
            {
                id
                name
                state
            }
            phoneNumber
            effectiveFrom
            effectiveTo
        }`, remittanceAddresses);
    const {hasPermission} = useUser();
    const createPermissionName = "RemittanceAddress.Create";
    const editPermissionName = "RemittanceAddress.Update";
    const deletePermissionName = "RemittanceAddress.Delete";
    const {
        selected,
        columns,
        newModal,
        editModal,
        deleteModal
    } = useTableModals(cols, editPermissionName, deletePermissionName);

    return (
        <>
            <AdvanceTableWrapper columns={columns}
                                 data={data}
                                 sortable
                                 pagination
                                 perPage={10}
            >
                <Card className={"mb-3"}>
                    <Card.Header>
                        <CardTableHeader onNewClick={newModal.onClick}
                                         title={"Remittance Addresses"}
                                         table
                                         showNew={hasPermission(createPermissionName)}
                        />
                    </Card.Header>
                    <Card.Body className={"p-0"}>
                        <AdvanceTable table
                                      headerClassName="bg-200 text-900 text-nowrap align-middle"
                                      rowClassName="align-middle white-space-nowrap"
                                      tableProps={{
                                          size: 'sm',
                                          striped: true,
                                          className: 'fs--1 mb-0 overflow-hidden'
                                      }}
                        />
                    </Card.Body>
                    <Card.Footer>
                        <AdvanceTablePagination table/>
                    </Card.Footer>
                </Card>
            </AdvanceTableWrapper>
            <NewRemittanceAddressModal showModal={newModal.showModal}
                                            onClose={newModal.onClose}
                                            billingGroupId={billingGroupId}
                                            availableLinesOfService={availableLinesOfService}
            />
            {selected && <EditRemittanceAddressModal showModal={editModal.showModal}
                                                          onClose={editModal.onClose}
                                                          selectedAddress={selected}
                                                          availableLinesOfService={availableLinesOfService}
            />}
            {selected && <DeleteModal mutation={deleteMutation}
                                      onClose={deleteModal.onClose}
                                      thingToDelete={"Remittance Address"}
                                      showModal={deleteModal.showModal}
                                      id={selected.id}>
                <p>This action cannot be undone.</p>
            </DeleteModal>}
        </>
    )
}
RemittanceAddresses.propTypes = {
    remittanceAddresses: PropTypes.array.isRequired,
    billingGroupId: PropTypes.string.isRequired,
    availableLinesOfService: PropTypes.array.isRequired
}
export default RemittanceAddresses
